import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { theme, cssVariablesResolver } from './theme/theme';
import { AuthProvider } from './auth/AuthProvider';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { LoginPage } from './auth/LoginPage';
import { EnrollmentPage } from './pages/EnrollmentPage';
import { MembershipsPage } from './pages/MembershipsPage';
import { Shell } from './shell/Shell';
import { OverviewPage } from './pages/OverviewPage';
import { PoolPage } from './pages/PoolPage';
import { PoolKeyPage } from './pages/PoolKeyPage';
import { LedgerPage } from './pages/LedgerPage';
import { EstatesPage } from './pages/EstatesPage';
import { EstatePage } from './pages/EstatePage';
import { TenantPage } from './pages/TenantPage';
import { MemoryPage } from './pages/MemoryPage';
import { MemoryOverviewPage } from './pages/MemoryOverviewPage';
import { ConsoleModals } from './features/modals';
import { Center, Loader } from '@mantine/core';
import { DEV_HARNESS_ENABLED } from './dev/flag';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
// Charts styles must follow the core stylesheet, or tooltips land in the wrong
// place and series lose their colours.
import '@mantine/charts/styles.css';
import './theme/global.css';

/** The screens, mounted once for the authed console and once — with a
 *  fixture source installed around them — for the preview harness. Sharing the
 *  element tree is the point: what the preview shows is what the console is. */
function ConsoleRoutes() {
  return (
    <>
      <Route index element={<OverviewPage />} />
      <Route path="pool" element={<PoolPage />} />
      <Route path="pool/:keyId" element={<PoolKeyPage />} />
      <Route path="ledger" element={<LedgerPage />} />
      <Route path="memory" element={<MemoryOverviewPage />} />
      <Route path="estates" element={<EstatesPage />} />
      <Route path="memberships" element={<MembershipsPage />} />
      <Route path="estates/:estateId" element={<EstatePage />} />
      <Route path="estates/:estateId/tenants/:tenantId" element={<TenantPage />} />
      <Route path="estates/:estateId/tenants/:tenantId/memory" element={<MemoryPage />} />
    </>
  );
}

// The dynamic import sits INSIDE the branch so a build with the flag off leaves
// it unreachable and the whole preview subtree — harness, fixture source,
// fixture data — is dropped rather than merely never loaded.
const DevRoute = DEV_HARNESS_ENABLED ? lazy(() => import('./dev/DevRoute')) : null;

export function App() {
  return (
    <MantineProvider
      theme={theme}
      cssVariablesResolver={cssVariablesResolver}
      forceColorScheme="light"
    >
      <Notifications position="top-right" />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/enroll" element={<EnrollmentPage />} />

            {DevRoute ? (
              <Route
                path="/dev"
                element={
                  <Suspense
                    fallback={
                      <Center h="100vh">
                        <Loader />
                      </Center>
                    }
                  >
                    <DevRoute />
                  </Suspense>
                }
              >
                {ConsoleRoutes()}
              </Route>
            ) : null}

            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Shell />
                  <ConsoleModals />
                </ProtectedRoute>
              }
            >
              {ConsoleRoutes()}
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </MantineProvider>
  );
}
